import { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Spinner from '../../shared/spinner';
import { Button, Form, Row, Col } from 'react-bootstrap';
import CustomAlert from '../../shared/customAlert';
import PromotoraFinanciamentoService from '../../services/promotoraFinanciamentoService';
import { useVerificaPermissao } from '../../shared/useVerificaPermissao';
import { ProtectedAction } from '../../AccessControl';
import { FiltroFinanciamentoDTO } from '../../shared/dto/filtroFinanciamentoDTO';
import { Financiamento } from '../../shared/model/financiamento';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FinanciamentoService from '../../services/financiamentoService';

type ItemLista = {
  id: string | number;
  nome: string;
  selected: boolean;
  originalRef: any;
};

function TabelaFinanciamentoPromotora() {
  useVerificaPermissao();
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'warning' | 'error' } | null>(null);
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  
  const [modoOrdenacao, setModoOrdenacao] = useState<'codigo' | 'nome' | ''>('nome');
  const [financiamentosDisp, setFinanciamentosDisp] = useState<ItemLista[]>([]);
  const [financiamentosSel, setFinanciamentosSel] = useState<ItemLista[]>([]);

  const service = useMemo(() => new PromotoraFinanciamentoService(), []);
  const financService = useMemo(() => new FinanciamentoService(), []);
  
  const { idPromotora, codigoFinanciamento, nomeFinanciamento } = location.state || {};

  const carregarListas = useCallback(async () => {
    setLoading(true);
    try {
      const filtro = new FiltroFinanciamentoDTO();
      filtro.status = "Ativos"; 
      const responseGeral = await financService.listarTodosFinanciamentos(0, 999, filtro);
      const listaGeral: Financiamento[] = responseGeral.data.content;

      const responseVinculados = await service.listarFinanciamentosPorPromotora(idPromotora);
      const listaVinculados: any[] = responseVinculados.data;
      
      const idsVinculados = listaVinculados.map(v => Number(v.idFinanciamento));

      const formatados: ItemLista[] = listaGeral.map(f => ({
        id: Number(f.id),
        nome: f.nome!,
        selected: false,
        originalRef: f
      }));

      setFinanciamentosSel(formatados.filter(f => idsVinculados.includes(Number(f.id))));
      setFinanciamentosDisp(formatados.filter(f => !idsVinculados.includes(Number(f.id))));

    } catch (err) {
      setAlert({ message: 'Erro ao carregar listas de financiamento.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [idPromotora, service, financService]);

  useEffect(() => {
    if (idPromotora) {
      carregarListas();
    }
  }, [carregarListas, idPromotora]);

  const ordenarLista = useCallback((lista: ItemLista[]) => {
    return [...lista].sort((a, b) => {
      if (modoOrdenacao === 'nome') return a.nome.localeCompare(b.nome);
      if (modoOrdenacao === 'codigo') return Number(a.id) - Number(b.id);
      return 0;
    });
  }, [modoOrdenacao]);

  const mover = (toRight: boolean, all: boolean) => {
    if (toRight) {
      const items = all ? financiamentosDisp : financiamentosDisp.filter(i => i.selected);
      setFinanciamentosSel([...financiamentosSel, ...items.map(i => ({ ...i, selected: false }))]);
      setFinanciamentosDisp(all ? [] : financiamentosDisp.filter(i => !i.selected));
    } else {
      const items = all ? financiamentosSel : financiamentosSel.filter(i => i.selected);
      setFinanciamentosDisp([...financiamentosDisp, ...items.map(i => ({ ...i, selected: false }))]);
      setFinanciamentosSel(all ? [] : financiamentosSel.filter(i => !i.selected));
    }
  };

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>, setList: any, list: ItemLista[]) => {
    const ids = Array.from(e.target.selectedOptions).map(opt => String(opt.value));
    setList(list.map(item => ({ ...item, selected: ids.includes(String(item.id)) })));
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = financiamentosSel.map(item => item.originalRef);
      await service.incluirAlterarPromotoraFinanciamento(payload, idPromotora);
      setAlert({ message: 'Vínculos atualizados com sucesso!', type: 'success' });
    } catch (err) {
      setAlert({ message: 'Erro ao salvar os vínculos.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const RenderDualList = ({ titulo, disponiveis, setDisponiveis, selecionados, setSelecionados }: any) => {
    const dispOrd = useMemo(() => ordenarLista(disponiveis), [disponiveis]);
    const selOrd = useMemo(() => ordenarLista(selecionados), [selecionados]);

    return (
      <div className="card mb-4 shadow-sm border-0">
        <div className="card-header bg-white border-bottom-0 pt-3"><strong>{titulo}</strong></div>
        <div className="card-body pt-0">
          <div className="d-flex justify-content-between gap-3 align-items-center">
            <div className="flex-grow-1" style={{ width: '45%' }}>
              <select multiple size={10} className="form-control" style={{ fontSize: '12px' }} 
                onChange={e => handleSelect(e, setDisponiveis, disponiveis)}>
                {dispOrd.map(i => (<option key={i.id} value={i.id}>{modoOrdenacao === 'codigo' ? `${String(i.id).padStart(6, '0')} - ` : ''}{i.nome}</option>))}
              </select>
            </div>
            <div className="d-flex flex-column gap-2 mt-4">
              <Button variant="outline-primary" size="sm" onClick={() => mover(true, false)}>{">"}</Button>
              <Button variant="outline-primary" size="sm" onClick={() => mover(true, true)}>{">>"}</Button>
              <Button variant="outline-primary" size="sm" onClick={() => mover(false, false)}>{"<"}</Button>
              <Button variant="outline-primary" size="sm" onClick={() => mover(false, true)}>{"<<"}</Button>
            </div>
            <div className="flex-grow-1" style={{ width: '45%' }}>
              {/* CORREÇÃO AQUI: Usando os nomes das props 'setSelecionados' e 'selecionados' */}
              <select multiple size={10} className="form-control" style={{ fontSize: '12px' }} 
                onChange={e => handleSelect(e, setSelecionados, selecionados)}>
                {selOrd.map(i => (<option key={i.id} value={i.id}>{modoOrdenacao === 'codigo' ? `${String(i.id).padStart(6, '0')} - ` : ''}{i.nome}</option>))}
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="position-relative m-md-3"> 
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ fontSize: '1.25rem' }}>Cadastro &gt; Promotoras &gt; Tabela de Financiamento</h5>
        <Button id="App-button-cor-principal" onClick={() => window.history.back()}>Voltar</Button>
      </div>

      {loading ? <Spinner loading={loading} /> : (
        <form onSubmit={handleSubmit}>
          <div className="card p-3 mb-3 bg-white border-0 shadow-sm">
            <Row>
              <Col md={2}>
                <Form.Label className="small text-muted mb-1">Código</Form.Label>
                <Form.Control disabled value={codigoFinanciamento || ''} style={{ backgroundColor: '#d1d1d1', color: '#000', fontWeight: 'bold' }} />
              </Col>
              <Col md={5}>
                <Form.Label className="small text-muted mb-1">Promotora</Form.Label>
                <Form.Control disabled value={nomeFinanciamento || ''} style={{ backgroundColor: '#d1d1d1', color: '#000', fontWeight: 'bold' }} />
              </Col>
            </Row>
          </div>

          <div className="card p-3 mb-3 bg-white border-0 shadow-sm">
            <label className="small fw-bold">Modo de Exibição das Listas</label>
            <select className="form-control mt-1" style={{ width: '250px' }} value={modoOrdenacao} onChange={(e) => setModoOrdenacao(e.target.value as any)}>
              <option value="nome">Ordem Alfabética (Nome)</option>
              <option value="codigo">Ordem de Código (ID)</option>
            </select>
          </div>

          <RenderDualList 
            titulo="Financiamentos Disponíveis / Selecionados" 
            disponiveis={financiamentosDisp} 
            setDisponiveis={setFinanciamentosDisp} 
            selecionados={financiamentosSel} 
            setSelecionados={setFinanciamentosSel} 
          />

          <div className="mt-4 pb-5">
            <ProtectedAction>
              <Button type="submit" variant="success" size="lg">
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Atualizar Lista
              </Button>
            </ProtectedAction>
          </div>
        </form>
      )}
    </div>
  );
}

export default TabelaFinanciamentoPromotora;